import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sortingApi } from './sorting.api';
import type {
  ScanContainerRequest,
  ScanPageRequest,
  RefuseContainerRequest
} from '../types';

export function useStartSortingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: number) => sortingApi.start(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sorting'] });
    }
  });
}

export function useScanContainerMutation(sessionId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ScanContainerRequest) =>
      sortingApi.scan(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sorting', 'detail', sessionId]
      });
      queryClient.invalidateQueries({ queryKey: ['sorting', 'metrics'] });
    }
  });
}

export function useScanPageMutation(sessionId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ScanPageRequest) => sortingApi.scanPage(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sorting', 'detail', sessionId]
      });
      queryClient.invalidateQueries({ queryKey: ['sorting', 'metrics'] });
    }
  });
}

export function useCompleteContainerMutation(sessionId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { item_id: number }) =>
      sortingApi.completeContainer(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sorting', 'detail', sessionId]
      });
      queryClient.invalidateQueries({ queryKey: ['sorting', 'metrics'] });
    }
  });
}

export function useRefuseContainerMutation(sessionId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RefuseContainerRequest) =>
      sortingApi.refuse(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sorting', 'detail', sessionId]
      });
      queryClient.invalidateQueries({ queryKey: ['sorting', 'metrics'] });
    }
  });
}

export function useCompleteSortingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: number) => sortingApi.complete(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sorting'] });
    }
  });
}

export function useBulkDeleteSortingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Record<string, unknown> }
    ) => sortingApi.bulkDelete(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sorting'] });
    }
  });
}
